/**
 * Waits for an element to appear in the document.
 *
 * Between one step and the next the target almost never exists yet: the route just changed, a
 * list is still loading, a drawer is opening. Querying the DOM right after navigation would always
 * fail, and waiting a fixed time would be either too much (slow) or too little (broken on a slow
 * connection).
 *
 * So this watches the document's mutations and stops as soon as the element exists. The timeout is
 * not an error — it means "the target never arrived" — and the tour shows that as a step with no
 * spotlight rather than getting stuck.
 */
export const waitForElement = (selector: string, timeoutMs = 8000): Promise<HTMLElement | null> => {
  const found = document.querySelector<HTMLElement>(selector)
  if (found) return Promise.resolve(found)

  return new Promise((resolve) => {
    let done = false

    const finish = (element: HTMLElement | null): void => {
      if (done) return
      done = true
      observer.disconnect()
      clearTimeout(timer)
      resolve(element)
    }

    const observer = new MutationObserver(() => {
      const element = document.querySelector<HTMLElement>(selector)
      if (element) finish(element)
    })

    observer.observe(document.body, { childList: true, subtree: true })
    const timer = setTimeout(() => finish(null), timeoutMs)
  })
}
